import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { formatDateToDDMMYYYY } from "./const";
import { toast } from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { listRefineries } from "../erpClient";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import { getSessionBranchId } from "../utils/sessionBranch";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import {
  normalizeListRefineryRows,
  isRefineryApiSuccess,
  refinerySocketPayloadToTableRow,
} from "../utils/refineryErpAdapter";
import RefineryPdfModal from "../components/RefineryPdfModal";

function RefineryTable() {
  const navigate = useNavigate();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedRefineryForPdf, setSelectedRefineryForPdf] = useState(null);

  const canCreate = hasPermission(PERMISSIONS.ADD_REFINERY);
  const canEdit = hasPermission(PERMISSIONS.EDIT_REFINERY);

  const pageCache = useVisitedPageCache();

  const fetchRows = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const branchId = getSessionBranchId();
      const cacheKey = visitedPageKey([branchId, page, limit, search]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setRows(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await listRefineries({
          search,
          pagination: { page, limit },
          ...(branchId != null ? { branch_id: branchId } : null),
        });

        if (!isRefineryApiSuccess(response)) {
          setRows([]);
          setTotalCount(0);
          return;
        }

        const list = normalizeListRefineryRows(response);
        list.sort((a, b) => Number(b.id) - Number(a.id));
        const apiTotal =
          response?.data?.pagination?.total ??
          response?.pagination?.total ??
          response?.total_count ??
          list.length;
        const total = Number(apiTotal) || list.length;
        setRows(list);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: list, total });
      } catch (error) {
        console.error("Error fetching refinery list:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch refinery data",
        );
        setRows([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchRows(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchRows]);

  useEffect(() => {
    const handleApiUrlChange = () => {
      setCurrentPage(1);
      fetchRows(1, itemsPerPage, searchQuery, true);
    };
    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => window.removeEventListener("apiUrlChanged", handleApiUrlChange);
  }, [itemsPerPage, searchQuery, fetchRows]);

  useEffect(() => {
    const socket = createErpSocket();

    const upsertRow = (data) => {
      const row = refinerySocketPayloadToTableRow(data);
      if (!row?.id) return;
      setRows((prev) => {
        const exists = prev.some((r) => String(r.id) === String(row.id));
        if (exists) {
          return prev.map((r) =>
            String(r.id) === String(row.id) ? { ...r, ...row } : r,
          );
        }
        return [row, ...prev].sort((a, b) => Number(b.id) - Number(a.id));
      });
    };

    socket.on("refinery_created", upsertRow);
    socket.on("refinery_updated", upsertRow);

    return () => {
      socket.off("refinery_created");
      socket.off("refinery_updated");
      socket.disconnect();
    };
  }, []);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Refinery Process</h4>
            {canCreate && (
              <button
                className={`btn btn-success ${buttonClass}`}
                disabled={!isCurrentFinancialYear}
                title={
                  !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                }
                onClick={() => {
                  if (!isCurrentFinancialYear) return;
                  navigate("/refinery/add");
                }}
              >
                Add Refinery
              </button>
            )}
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "320px" }}
                placeholder="Search doc no / supplier / remarks..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Doc No</th>
                    <th>Date</th>
                    <th>Supplier</th>
                    <th>Items</th>
                    <th>Total Fine Wt</th>
                    <th>Total Pcs</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length > 0 ? (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.doc_number}</td>
                        <td>{formatDateToDDMMYYYY(row.doc_date)}</td>
                        <td>{row.supplier_name || "-"}</td>
                        <td>{row.item_count}</td>
                        <td>{row.total_fine_wt || "-"}</td>
                        <td>{row.total_pcs ?? "-"}</td>
                        <td
                          style={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.remarks || ""}
                        >
                          {row.remarks || "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              title="Voucher"
                              onClick={() => {
                                setSelectedRefineryForPdf(row);
                                setShowPdfModal(true);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.172 3.172C3 4.343 3 6.229 3 10v4c0 3.771 0 5.657 1.172 6.828S7.229 22 11 22h2c3.771 0 5.657 0 6.828-1.172S21 17.771 21 14v-4c0-3.771 0-5.657-1.172-6.828S16.771 2 13 2h-2C7.229 2 5.343 2 4.172 3.172M7.25 8A.75.75 0 0 1 8 7.25h8a.75.75 0 0 1 0 1.5H8A.75.75 0 0 1 7.25 8m0 4a.75.75 0 0 1 .75-.75h8a.75.75 0 0 1 0 1.5H8a.75.75 0 0 1-.75-.75M8 15.25a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                            {canEdit && (
                              <button
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                title="Edit"
                                onClick={() =>
                                  navigate(`/refinery/${row.id}/edit`)
                                }
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No refinery bills found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      <RefineryPdfModal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        refineryId={selectedRefineryForPdf?.id}
      />
    </div>
  );
}

export default RefineryTable;
